import { useContext } from 'react'

import {
  type SanityBlogPostWithoutBody,
  type SanityBlogPostOptions,
} from '@data/sanity/queries/types/blog'
import { getFormattedDate } from '@lib/format'
import { LanguageContext } from '@lib/language-context'
import { getLinkPageUrl } from '@lib/routes'
import { StringsContext } from '@lib/strings-context'

import Photo from '@components/photo'
import SimpleLink from '@components/simple-link'

interface BlogPostHeaderProps {
  post: SanityBlogPostWithoutBody
  options?: SanityBlogPostOptions
}

const BlogPostHeader = ({ post, options }: BlogPostHeaderProps) => {
  const { locale } = useContext(LanguageContext)
  const strings = useContext(StringsContext)

  const blogAuthorUrl = getLinkPageUrl({
    pageType: 'blogAuthor',
    slugs: post.author?.slug.current,
  })

  return (
    <section className="container pt-14 sm:pt-20 md:pt-24 lg:pt-40 flex flex-col justify-center items-center">
      <div className="w-full max-w-3xl mb-6">
        <h1 className="mb-4">{post.title}</h1>

        {!!options?.showExcerpt && (
          <p className="text-xl mb-6">{post.excerpt}</p>
        )}

        {!!options?.showCategories && (
          <div className="flex flex-wrap mt-4">
            {post.categories
              ?.filter((category) => category)
              ?.map((category) => (
                <SimpleLink
                  key={`${post.slug.current}-category-${category.slug.current}`}
                  href={getLinkPageUrl({
                    pageType: 'blogCategory',
                    slugs: category.slug.current,
                  })}
                  className="mr-2 text-xs underline hover:text-orange"
                >
                  {category.name}
                </SimpleLink>
              ))}
          </div>
        )}

        {(!!options?.showAuthorPhoto ||
          !!options?.showAuthorName ||
          !!options?.showDate) && (
          <div className="flex items-center mt-4">
            {!!options?.showAuthorPhoto && !!post.author?.photo && (
              <Photo
                image={post.author?.photo}
                width={40}
                height={40}
                className="rounded-full overflow-hidden shrink-0 w-[40px] h-[40px] mr-2"
                imageClassName="object-cover"
              />
            )}
            <div>
              {options?.showAuthorName && (
                <p className="text-sm">
                  {strings.blogPostBy}{' '}
                  <SimpleLink href={blogAuthorUrl}>
                    {post.author?.name}
                  </SimpleLink>
                  {!!options?.showDate
                    ? `, ${getFormattedDate(
                        post.date ?? post._createdAt,
                        locale
                      )}`
                    : ''}
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      {!!post.coverImage && !!options?.showCoverImage && (
        <div className="mt-6">
          <Photo
            image={{
              ...post.coverImage,
              borderRadius: post.coverImage.borderRadius ?? 'xl',
            }}
          />
        </div>
      )}
    </section>
  )
}

export default BlogPostHeader
